import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaMoneyBillWave,
  FaInfoCircle,
  FaCheckCircle,
  FaHandsHelping,
  FaCalendarAlt,
  FaHeartbeat,
  FaHome,
  FaClipboardList,
} from "react-icons/fa";
import config, { getUserCity, fetchRemoteConfig } from "../../../config";
import TermsAcceptStep from "./TermsAcceptStep";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

// Elder Care Services Needed — grouped exactly as the care team presents
// them on the intake form, so the checkboxes read the same way in the app
// as they do on paper.
export const ELDER_CARE_GROUPS = [
  {
    group: "Personal Care",
    options: [
      { value: "bathing-assistance", label: "Bathing Assistance" },
      { value: "dressing", label: "Dressing" },
      { value: "grooming", label: "Grooming" },
      { value: "toileting-assistance", label: "Toileting Assistance" },
      { value: "feeding-assistance", label: "Feeding Assistance" },
      { value: "personal-hygiene", label: "Personal Hygiene" },
      { value: "oral-hygiene", label: "Oral Hygiene" },
    ],
  },
  {
    group: "Mobility",
    options: [
      { value: "walking-assistance", label: "Walking Assistance" },
      { value: "wheelchair-assistance", label: "Wheelchair Assistance" },
      { value: "bed-to-chair-assistance", label: "Bed-to-Chair Assistance" },
      { value: "fall-prevention", label: "Fall Prevention" },
    ],
  },
  {
    group: "Daily Support",
    options: [
      { value: "meal-serving", label: "Meal Serving" },
      { value: "water-food-reminder", label: "Water/Food Reminder" },
      { value: "medicine-reminder", label: "Medicine Reminder" },
      { value: "bed-making", label: "Bed Making" },
      { value: "daily-routine-assistance", label: "Daily Routine Assistance" },
    ],
  },
  {
    group: "Companionship",
    options: [
      { value: "conversation", label: "Conversation" },
      { value: "reading", label: "Reading" },
      { value: "watching-tv", label: "Watching TV" },
      { value: "indoor-activities", label: "Indoor Activities" },
      { value: "general-companionship", label: "General Companionship" },
    ],
  },
  {
    group: "Supervision",
    options: [
      { value: "safety-observation", label: "Safety Observation" },
      { value: "night-supervision", label: "Night Supervision" },
      { value: "dementia-memory-supervision", label: "Dementia/Memory Supervision" },
    ],
  },
  {
    group: "Outside Support",
    options: [
      { value: "doctor-appointment", label: "Doctor Appointment" },
      { value: "hospital-accompaniment", label: "Hospital Accompaniment" },
      { value: "pharmacy-essential-errand", label: "Pharmacy/Essential Errand" },
    ],
  },
];

// Flat lookup used for the summary chips + submit payload.
const ELDER_CARE_OPTIONS = ELDER_CARE_GROUPS.flatMap((g) => g.options);

const MOBILITY_STATUS_OPTIONS = [
  "Fully Mobile",
  "Needs Assistance",
  "Wheelchair-bound",
  "Bedridden",
];

const LIVING_SITUATION_OPTIONS = [
  "Lives Alone",
  "Lives with Family",
  "Lives with Spouse",
  "Assisted Living Facility",
];

const CARE_REQUIREMENT_OPTIONS = [
  "Basic Care",
  "Moderate Care",
  "Full-Time Care",
  "Critical / Post-Hospitalisation Care",
];

const RELATIONSHIP_OPTIONS = [
  "Self",
  "Parent",
  "Grandparent",
  "Spouse",
  "Relative",
  "Other",
];

// Display labels for the Summary section — the <select>s store/send the
// raw option values, these just render them back as readable text.
const SHIFT_TIME_LABELS = { morning: "Morning", afternoon: "Afternoon", evening: "Evening", full_day: "Full Day" };
const SHIFT_DURATION_LABELS = { "2hrs": "2 Hours", "4hrs": "4 Hours", "6hrs": "6 Hours", "12hrs": "12 Hours", live_24hrs: "Live-in (24 Hours)" };

const ElderCaretaker = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [step, setStep] = useState("form"); // "form" -> "terms"

  const categoryKey = config.HOME_SERVICES_CATEGORY || "care-services";
  const serviceKey = config.ELDER_CARETAKER_SERVICE || "elder-caretaker";

  // City/Area come from the admin-configured backend list (config_data
  // collection), not a hardcoded array — config.AREA_OPTIONS starts as a
  // local bootstrap default and gets merged with the live backend values
  // on app load, but that mutation isn't reactive on its own, so this
  // re-syncs local state once the fetch resolves (a no-op re-fetch if
  // another page already triggered it this session).
  const [areaOptions, setAreaOptions] = useState(config.AREA_OPTIONS || {});
  useEffect(() => {
    fetchRemoteConfig().then(() => setAreaOptions({ ...config.AREA_OPTIONS }));
  }, []);
  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
    config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];

  const serviceLabel = selectedService?.label || "Elder Caretaker";
  const categoryLabel = selectedCategory?.label || "Home Services";

  const [formData, setFormData] = useState({
    // Requester's own contact details
    username: "",
    mobilenumber: "",
    email: "",
    address: "",
    city: "",
    area: "",
    language: "",

    // About the elder
    elderName: "",
    elderAge: "",
    elderGender: "",
    relationship: "",
    mobilityStatus: "",
    physicalCondition: "",
    livingSituation: "",
    careRequirements: "",
    specialInstructions: "",

    // Elder care services needed — every listed service is included
    // automatically (see the "no selection" change below), so this starts
    // pre-populated rather than empty.
    service_type: ELDER_CARE_OPTIONS.map((o) => o.value),

    // Schedule & caretaker preference
    gender: "",
    urgencyStatus: "",
    startDate: "",
    duration: "",
    shiftTime: "",
    shiftDuration: "",
    salaryOffered: "",
  });

  const availableAreas = useMemo(() => {
    const selected =
      areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
    if (Array.isArray(selected)) return selected;
    if (selected?.areas) return selected.areas;
    return [];
  }, [formData.city, areaOptions]);

  const styles = {
    page: {
      minHeight: "100vh",
      background:
        "linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eef2ff 100%)",
      padding: "30px 20px",
      fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
    },
    wrapper: {
      maxWidth: "1580px",
      width: "96%",
      margin: "0 auto",
    },
    hero: {
      background: "linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)",
      color: "#fff",
      borderRadius: "28px",
      padding: "34px 36px",
      boxShadow: "0 20px 40px rgba(37, 99, 235, 0.22)",
      marginBottom: "24px",
    },
    heroBadge: {
      display: "inline-block",
      background: "rgba(255,255,255,0.14)",
      padding: "7px 14px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: "700",
      marginBottom: "12px",
    },
    heroTitle: {
      margin: 0,
      fontSize: "2.1rem",
      fontWeight: "800",
    },
    heroText: {
      marginTop: "10px",
      marginBottom: 0,
      color: "rgba(255,255,255,0.9)",
      maxWidth: "900px",
      lineHeight: 1.6,
      fontSize: "15px",
    },
    formCard: {
      background: "#fff",
      borderRadius: "28px",
      border: "1px solid #e5edf8",
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
      overflow: "hidden",
      width: "100%",
      maxWidth: "1320px",
      margin: "0 auto",
    },
    formHeader: {
      padding: "28px 40px",
      borderBottom: "1px solid #edf2f7",
      background: "linear-gradient(180deg, #ffffff, #f8fbff)",
    },
    formTitle: {
      margin: 0,
      fontSize: "1.6rem",
      fontWeight: "800",
      color: "#0f172a",
    },
    formSub: {
      margin: "8px 0 0 0",
      color: "#64748b",
      fontSize: "14px",
    },
    body: {
      padding: "32px 40px 36px",
    },
    section: {
      marginBottom: "28px",
      padding: "24px",
      borderRadius: "20px",
      background: "#f8fbff",
      border: "1px solid #e6eef8",
    },
    sectionTitle: {
      margin: "0 0 18px 0",
      fontSize: "1.1rem",
      fontWeight: "800",
      color: "#0f172a",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "20px",
      alignItems: "start",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    groupHeading: {
      fontSize: "13px",
      fontWeight: "800",
      color: "#1d4ed8",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      marginBottom: "8px",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      minHeight: "120px",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      fontSize: "15px",
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
      resize: "vertical",
    },
    submitWrap: {
      display: "flex",
      justifyContent: "center",
      marginTop: "30px",
    },
    submitBtn: {
      border: "none",
      borderRadius: "999px",
      background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
      color: "#fff",
      padding: "15px 34px",
      fontSize: "15px",
      fontWeight: "800",
      minWidth: "260px",
      boxShadow: "0 12px 24px rgba(37, 99, 235, 0.24)",
      cursor: "pointer",
    },
    helper: {
      color: "#64748b",
      fontSize: "13px",
      margin: 0,
      lineHeight: 1.6,
    },
    serviceGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "10px",
      marginTop: "10px",
    },
    serviceCell: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 14px",
      borderRadius: "12px",
      border: "1px solid #dbe7f5",
      background: "#fff",
    },
    serviceCellLabel: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#0f172a",
    },
    errorText: {
      color: "#dc2626",
      fontSize: "14px",
      fontWeight: "600",
      marginTop: "12px",
    },
    popupOverlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    },
    popupCard: {
      background: "#fff",
      borderRadius: "18px",
      padding: "22px 24px",
      maxWidth: "360px",
      width: "90%",
      textAlign: "center",
      boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
    },
    popupTitle: {
      margin: "10px 0 6px 0",
      fontSize: "1.1rem",
      fontWeight: "800",
      color: "#065f46",
    },
    popupText: {
      margin: 0,
      fontSize: "14px",
      color: "#4b5563",
    },
    popupButton: {
      marginTop: "16px",
      borderRadius: "999px",
      border: "none",
      background: "#16a34a",
      color: "#fff",
      fontSize: "14px",
      fontWeight: "700",
      padding: "10px 24px",
      cursor: "pointer",
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanValue =
      name === "mobilenumber" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
      ...(name === "city" ? { area: "" } : {}),
    }));
  };

  // Step 1 -> Step 2: validate the form, then show Terms & Conditions.
  // The actual POST only happens from submitRequirement(), reached via the
  // Terms step's checkbox + Submit button — see TermsAcceptStep.
  const handleNext = (e) => {
    e.preventDefault();
    setSubmitError("");
    setStep("terms");
  };

  const submitRequirement = async () => {
    setSubmitError("");

    const detailedServiceList = ELDER_CARE_GROUPS.flatMap((g) =>
      g.options
        .filter((item) => formData.service_type.includes(item.value))
        .map((item) => ({
          type: "elder-care",
          group: g.group,
          key: item.value,
          value: item.value,
          label: item.label,
        }))
    );

    const requestBody = {
      // Requester's own contact details
      username: formData.username,
      mobilenumber: formData.mobilenumber,
      email: formData.email,
      address: formData.address,
      city: formData.city,
      area: formData.area,
      language: formData.language,

      // About the elder
      elderName: formData.elderName,
      elderAge: formData.elderAge,
      elderGender: formData.elderGender,
      relationship: formData.relationship,
      mobilityStatus: formData.mobilityStatus,
      physicalCondition: formData.physicalCondition,
      livingSituation: formData.livingSituation,
      careRequirements: formData.careRequirements,
      specialInstructions: formData.specialInstructions,

      // Elder care services needed
      service_list: formData.service_type,
      detailed_service_list: detailedServiceList,
      service_type: "eldercare-services",

      // Schedule & caretaker preference
      gender: formData.gender,
      urgencyStatus: formData.urgencyStatus,
      startDate: formData.startDate,
      duration: formData.duration,
      shiftTime: formData.shiftTime,
      shiftDuration: formData.shiftDuration,
      salaryOffered: formData.salaryOffered,

      service_category: categoryKey,
      selected_city: getUserCity(),
      service: serviceKey,
      category_label: categoryLabel,
      service_label: serviceLabel,
      terms_accepted: true,
    };

    try {
      setIsSubmitting(true);

      const res = await fetch(`${BASE_URL}/api/requirements/elder-caretaker/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();

      if (res.ok && data?.success !== false) {
        setShowThankYou(true);
        setStep("form");
        setFormData({
          username: "",
          mobilenumber: "",
          email: "",
          address: "",
          city: "",
          area: "",
          language: "",
          elderName: "",
          elderAge: "",
          elderGender: "",
          relationship: "",
          mobilityStatus: "",
          physicalCondition: "",
          livingSituation: "",
          careRequirements: "",
          specialInstructions: "",
          service_type: ELDER_CARE_OPTIONS.map((o) => o.value),
          gender: "",
          urgencyStatus: "",
          startDate: "",
          duration: "",
          shiftTime: "",
          shiftDuration: "",
          salaryOffered: "",
        });
      } else {
        setSubmitError(data?.message || data?.error || "Failed to submit requirement.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCareLabels = ELDER_CARE_OPTIONS.filter((item) =>
    formData.service_type.includes(item.value)
  ).map((item) => item.label);

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#16a34a" size={32} />
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>
              Your requirement has been submitted. We will get back to you
              shortly.
            </p>
            <button
              type="button"
              style={styles.popupButton}
              onClick={() => {
                setShowThankYou(false);
                navigate("/");
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>
          <div style={styles.hero}>
            <div style={styles.heroBadge}>{serviceLabel}</div>
            <h1 style={styles.heroTitle}>
              Hire an Elder Caretaker
            </h1>
            <p style={styles.heroText}>
              Post your elder caretaker requirement with location details
              and salary offer — every relevant care service is included.
            </p>
          </div>

          {step === "form" && (
          <form style={styles.formCard} onSubmit={handleNext}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>
                  Elder Caretaker Requirement Form
                </h2>
                <p style={styles.formSub}>
                  Fill in your personal details, review the included elder
                  care services, and mention your salary offer.
                </p>
              </div>

              <div style={styles.body}>
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Your Contact Details</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="username" style={styles.label}>
                        <FaUser /> Your Name
                      </label>
                      <input
                        id="username"
                        style={styles.input}
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="mobilenumber" style={styles.label}>
                        <FaPhoneAlt /> Mobile Number
                      </label>
                      <input
                        id="mobilenumber"
                        style={styles.input}
                        type="tel"
                        name="mobilenumber"
                        value={formData.mobilenumber}
                        onChange={handleInputChange}
                        pattern="[0-9]{10}"
                        minLength={10}
                        maxLength={10}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="email" style={styles.label}>
                        <FaEnvelope /> Email
                      </label>
                      <input
                        id="email"
                        style={styles.input}
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="address" style={styles.label}>
                        <FaMapMarkerAlt /> Address
                      </label>
                      <input
                        id="address"
                        style={styles.input}
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="city" style={styles.label}>
                        <FaMapMarkerAlt /> City
                      </label>
                      <select
                        id="city"
                        style={styles.select}
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="area" style={styles.label}>
                        <FaMapMarkerAlt /> Area
                      </label>
                      <select
                        id="area"
                        style={styles.select}
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.city}
                      >
                        <option value="">Select Area</option>
                        {availableAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="language" style={styles.label}>
                        <FaLanguage /> Preferred Language
                      </label>
                      <input
                        id="language"
                        style={styles.input}
                        type="text"
                        name="language"
                        placeholder="Hindi, English, Kannada etc"
                        value={formData.language}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <FaHeartbeat />
                    About the Elder
                  </h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="elderName" style={styles.label}>
                        <FaUser /> Elder Name
                      </label>
                      <input
                        id="elderName"
                        style={styles.input}
                        type="text"
                        name="elderName"
                        value={formData.elderName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="elderAge" style={styles.label}>
                        <FaUser /> Age
                      </label>
                      <input
                        id="elderAge"
                        style={styles.input}
                        type="number"
                        name="elderAge"
                        min="1"
                        max="120"
                        value={formData.elderAge}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="elderGender" style={styles.label}>
                        <FaUserTag /> Gender
                      </label>
                      <select
                        id="elderGender"
                        style={styles.select}
                        name="elderGender"
                        value={formData.elderGender}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="relationship" style={styles.label}>
                        <FaUserTag /> Relationship to You
                      </label>
                      <select
                        id="relationship"
                        style={styles.select}
                        name="relationship"
                        value={formData.relationship}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        {RELATIONSHIP_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="mobilityStatus" style={styles.label}>
                        <FaHandsHelping /> Mobility Status
                      </label>
                      <select
                        id="mobilityStatus"
                        style={styles.select}
                        name="mobilityStatus"
                        value={formData.mobilityStatus}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        {MOBILITY_STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="physicalCondition" style={styles.label}>
                        <FaHeartbeat /> Physical Condition
                      </label>
                      <input
                        id="physicalCondition"
                        style={styles.input}
                        type="text"
                        name="physicalCondition"
                        placeholder="e.g. Diabetes, Arthritis, Post-surgery recovery"
                        value={formData.physicalCondition}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="livingSituation" style={styles.label}>
                        <FaHome /> Living Situation
                      </label>
                      <select
                        id="livingSituation"
                        style={styles.select}
                        name="livingSituation"
                        value={formData.livingSituation}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        {LIVING_SITUATION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="careRequirements" style={styles.label}>
                        <FaClipboardList /> Care Requirements
                      </label>
                      <select
                        id="careRequirements"
                        style={styles.select}
                        name="careRequirements"
                        value={formData.careRequirements}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        {CARE_REQUIREMENT_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ marginTop: "20px" }}>
                    <label htmlFor="specialInstructions" style={styles.label}>
                      <FaInfoCircle /> Special Instructions
                    </label>
                    <textarea
                      id="specialInstructions"
                      style={styles.textarea}
                      name="specialInstructions"
                      placeholder="Medicine timing, allergies, dementia/memory notes, behavioural cues, anything the caretaker should know."
                      value={formData.specialInstructions}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <FaHandsHelping />
                    Elder Care Services Included
                  </h3>

                  <p style={styles.helper}>
                    Every service below, across all care types, is included for this booking.
                  </p>

                  {ELDER_CARE_GROUPS.map((g) => (
                    <div key={g.group} style={{ marginTop: "16px" }}>
                      <div style={styles.groupHeading}>{g.group}</div>
                      <div style={styles.serviceGrid}>
                        {g.options.map((item) => (
                          <div key={item.value} style={styles.serviceCell}>
                            <FaCheckCircle color="#16a34a" size={14} />
                            <span style={styles.serviceCellLabel}>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <FaCalendarAlt />
                    Schedule & Caretaker Preference
                  </h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="gender" style={styles.label}>
                        <FaUserTag /> Preferred Caretaker Gender
                      </label>
                      <select
                        id="gender"
                        style={styles.select}
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="urgencyStatus" style={styles.label}>
                        <FaClock /> Urgency
                      </label>
                      <select
                        id="urgencyStatus"
                        style={styles.select}
                        name="urgencyStatus"
                        value={formData.urgencyStatus}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="within_days">Within Days</option>
                        <option value="within_months">Within Months</option>
                        <option value="just_enquiry">Just Enquiry</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="startDate" style={styles.label}>
                        <FaCalendarAlt /> Starting Date
                      </label>
                      <input
                        id="startDate"
                        style={styles.input}
                        type="date"
                        name="startDate"
                        min={new Date().toISOString().slice(0, 10)}
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="duration" style={styles.label}>
                        <FaClock /> Duration
                      </label>
                      <input
                        id="duration"
                        style={styles.input}
                        type="text"
                        name="duration"
                        placeholder="In months"
                        value={formData.duration}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ ...styles.grid, marginTop: "20px" }}>
                    <div style={styles.field}>
                      <label htmlFor="shiftTime" style={styles.label}>
                        <FaClock /> Shift
                      </label>
                      <select
                        id="shiftTime"
                        style={styles.select}
                        name="shiftTime"
                        value={formData.shiftTime}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="full_day">Full Day</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="shiftDuration" style={styles.label}>
                        <FaClock /> Hours
                      </label>
                      <select
                        id="shiftDuration"
                        style={styles.select}
                        name="shiftDuration"
                        value={formData.shiftDuration}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="2hrs">2 Hours</option>
                        <option value="4hrs">4 Hours</option>
                        <option value="6hrs">6 Hours</option>
                        <option value="12hrs">12 Hours</option>
                        <option value="live_24hrs">Live-in (24 Hours)</option>
                      </select>
                    </div>

                    <div style={styles.field}>
                      <label htmlFor="salaryOffered" style={styles.label}>
                        <FaMoneyBillWave /> Salary Offer
                      </label>
                      <input
                        id="salaryOffered"
                        style={styles.input}
                        type="text"
                        name="salaryOffered"
                        placeholder="Per month, e.g. 15000 rs/month"
                        value={formData.salaryOffered}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <FaInfoCircle />
                    Summary
                  </h3>
                  <p style={styles.helper}>
                    Review the selected elder care services before submitting.
                  </p>

                  <div
                    style={{
                      marginTop: "12px",
                      fontSize: "14px",
                      color: "#0f172a",
                    }}
                  >
                    {selectedCareLabels.length > 0 && (
                      <p>
                        <strong>Selected Services:</strong>{" "}
                        {selectedCareLabels.join(", ")}
                      </p>
                    )}

                    <p>
                      <strong>Schedule:</strong>{" "}
                      {formData.shiftDuration ? SHIFT_DURATION_LABELS[formData.shiftDuration] : "—"}
                      {formData.shiftTime && ` · ${SHIFT_TIME_LABELS[formData.shiftTime]}`}
                      {formData.duration && ` · ${formData.duration}`}
                    </p>

                    {formData.salaryOffered && (
                      <p>
                        <strong>Salary Offer:</strong> {formData.salaryOffered}
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p style={styles.errorText}>{submitError}</p>
                  )}
                </div>

                <div style={styles.submitWrap}>
                  <button
                    type="submit"
                    style={styles.submitBtn}
                    disabled={isSubmitting}
                  >
                    Next
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === "terms" && (
            <div style={styles.formCard}>
              <TermsAcceptStep
                onBack={() => setStep("form")}
                onSubmit={submitRequirement}
                isSubmitting={isSubmitting}
                submitError={submitError}
                serviceLabel={serviceLabel}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ElderCaretaker;